import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { membershipApplicationAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { ArrowLeft, Edit, Calendar, FileText, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../../components/UI/Modal';
import MembershipApplicationFormPreview from './MembershipApplicationFormPreview';

const Container = styled.div`
  padding: 2rem;
  background-color: ${props => props.theme.colors.background.primary};
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const ViewCard = styled(Card)`
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.text.secondary};
`;

const InfoValue = styled.span`
  font-size: 1rem;
  color: ${props => props.theme.colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'open') return props.theme.colors.success + '20';
    if (props.status === 'closed') return props.theme.colors.error + '20';
    if (props.status === 'scheduled') return props.theme.colors.warning + '20';
    return props.theme.colors.gray[100];
  }};
  color: ${props => {
    if (props.status === 'open') return props.theme.colors.success;
    if (props.status === 'closed') return props.theme.colors.error;
    if (props.status === 'scheduled') return props.theme.colors.warning;
    return props.theme.colors.text.secondary;
  }};
`;

const FieldsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 8px;
  padding: 1rem;
  background: ${props => props.theme.colors.white};
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.5rem;
`;

const FieldTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FieldMeta = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const FieldDescription = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-top: 0.5rem;
`;

const MembershipApplicationFormView = () => {
  const { theme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchForm();
  }, [id]);

  const fetchForm = async () => {
    setLoading(true);
    try {
      const res = await membershipApplicationAPI.getForm(id);
      setForm(res.data);
    } catch (error) {
      toast.error('Failed to load form');
      navigate('/app/membership-applications/forms');
    } finally {
      setLoading(false);
    }
  };

  const getFormStatus = (form) => {
    if (!form?.is_active) return 'inactive';
    const now = new Date();
    if (form.opens_at && new Date(form.opens_at) > now) return 'scheduled';
    if (form.closes_at && new Date(form.closes_at) < now) return 'closed';
    return 'open';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Short Text',
      textarea: 'Long Text',
      email: 'Email',
      tel: 'Phone',
      number: 'Number',
      date: 'Date',
      select: 'Dropdown',
      radio: 'Multiple Choice',
      checkbox: 'Checkboxes',
      file: 'File Upload',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <Container theme={theme}>
        <div>Loading...</div>
      </Container>
    );
  }

  if (!form) {
    return (
      <Container theme={theme}>
        <div>Form not found</div>
      </Container>
    );
  }

  const status = getFormStatus(form);

  return (
    <Container theme={theme}>
      <Header theme={theme}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/membership-applications/forms')}
            leftIcon={<ArrowLeft size={16} />}
          >
            Back
          </Button>
          <Title theme={theme}>{form.title}</Title>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <Button
            variant="outline"
            onClick={() => setShowPreview(true)}
            leftIcon={<Eye size={16} />}
          >
            Preview Form
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/app/membership-applications/forms/${form.id}/edit`)}
            leftIcon={<Edit size={16} />}
          >
            Edit Form
          </Button>
        </div>
      </Header>

      <ViewCard theme={theme}>
        <SectionTitle theme={theme}>Form Information</SectionTitle>
        <InfoGrid theme={theme}>
          <InfoItem theme={theme}>
            <InfoLabel theme={theme}>Status</InfoLabel>
            <StatusBadge status={status} theme={theme}>
              {status === 'open' && <CheckCircle size={12} style={{ marginRight: '0.25rem' }} />}
              {status === 'closed' && <XCircle size={12} style={{ marginRight: '0.25rem' }} />}
              {status === 'scheduled' && <Clock size={12} style={{ marginRight: '0.25rem' }} />}
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </StatusBadge>
          </InfoItem>
          <InfoItem theme={theme}>
            <InfoLabel theme={theme}>Type</InfoLabel>
            <InfoValue theme={theme}>
              {form.type === 'public' ? 'Public Application' :
               form.type === 'recommendation' ? 'Recommendation Form' :
               form.type === 'registration' ? 'Registration Form' :
               form.type}
            </InfoValue>
          </InfoItem>
          <InfoItem theme={theme}>
            <InfoLabel theme={theme}>Active</InfoLabel>
            <InfoValue theme={theme}>
              {form.is_active ? 'Yes' : 'No'}
            </InfoValue>
          </InfoItem>
          <InfoItem theme={theme}>
            <InfoLabel theme={theme}>Total Fields</InfoLabel>
            <InfoValue theme={theme}>{form.fields?.length || 0}</InfoValue>
          </InfoItem>
          <InfoItem theme={theme}>
            <InfoLabel theme={theme}>Total Submissions</InfoLabel>
            <InfoValue theme={theme}>{form.submissions?.length || 0}</InfoValue>
          </InfoItem>
        </InfoGrid>

        {(form.opens_at || form.closes_at) && (
          <InfoGrid theme={theme}>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Calendar size={14} />
                Opens At
              </InfoLabel>
              <InfoValue theme={theme}>{formatDate(form.opens_at)}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Calendar size={14} />
                Closes At
              </InfoLabel>
              <InfoValue theme={theme}>{formatDate(form.closes_at)}</InfoValue>
            </InfoItem>
          </InfoGrid>
        )}
      </ViewCard>

      {form.description && (
        <ViewCard theme={theme}>
          <SectionTitle theme={theme}>Description</SectionTitle>
          <p style={{ color: theme.colors.text.secondary, lineHeight: '1.6', margin: 0 }}>
            {form.description}
          </p>
        </ViewCard>
      )}

      {form.instructions && (
        <ViewCard theme={theme}>
          <SectionTitle theme={theme}>Instructions</SectionTitle>
          <p style={{ color: theme.colors.text.secondary, lineHeight: '1.6', margin: 0 }}>
            {form.instructions}
          </p>
        </ViewCard>
      )}

      <ViewCard theme={theme}>
        <SectionTitle theme={theme}>
          <FileText size={18} style={{ marginRight: '0.5rem' }} />
          Form Fields ({form.fields?.length || 0})
        </SectionTitle>
        <FieldsList theme={theme}>
          {form.fields && form.fields.length > 0 ? (
            form.fields.map((field, index) => (
              <FieldCard key={field.id || index} theme={theme}>
                <FieldHeader theme={theme}>
                  <FieldTitle theme={theme}>
                    {index + 1}. {field.field_label}
                    {field.is_required && (
                      <span style={{ color: theme.colors.error }}>*</span>
                    )}
                  </FieldTitle>
                </FieldHeader>
                <FieldMeta theme={theme}>
                  <span>Type: <strong>{getFieldTypeLabel(field.field_type)}</strong></span>
                  {field.field_type === 'select' || field.field_type === 'radio' || field.field_type === 'checkbox' ? (
                    <span>
                      Options: <strong>{Array.isArray(field.options) ? field.options.length : 0}</strong>
                    </span>
                  ) : null}
                  {field.placeholder && (
                    <span>Placeholder: <strong>{field.placeholder}</strong></span>
                  )}
                </FieldMeta>
                {field.help_text && (
                  <FieldDescription theme={theme}>
                    <em>{field.help_text}</em>
                  </FieldDescription>
                )}
                {field.options && Array.isArray(field.options) && field.options.length > 0 && (
                  <div style={{ marginTop: '0.75rem', paddingLeft: '1rem' }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', color: theme.colors.text.secondary }}>
                      Options:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', color: theme.colors.text.secondary }}>
                      {field.options.map((option, optIdx) => (
                        <li key={optIdx}>{option}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </FieldCard>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
              No fields added to this form yet.
            </div>
          )}
        </FieldsList>
        </ViewCard>

      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        title="Form Preview"
        size="large"
      >
        <MembershipApplicationFormPreview formId={form.id} onClose={() => setShowPreview(false)} />
      </Modal>
    </Container>
  );
};

export default MembershipApplicationFormView;


